import apiClient from './client'

export interface JobEnqueuedResponse {
  job_id: string
  message: string
}

export interface JobStatusResponse {
  job_id: string
  status: string
  progress: number | null
  result: Record<string, unknown> | null
}

export interface JobSummary {
  job_id: string
  function: string
  status: string
  queue: string
  queued_at: number | null
  scheduled_at: number | null
  started_at: number | null
  completed_at: number | null
  error: string | null
}

export interface QueueStatsResponse {
  queued: number
  active: number
  scheduled: number
  worker_count: number
  jobs: JobSummary[]
}

export const adminApi = {
  async triggerEnrich(years: string): Promise<JobEnqueuedResponse> {
    const response = await apiClient.post<JobEnqueuedResponse>('/admin/jobs/enrich', null, {
      params: { years }
    })
    return response.data
  },

  async triggerAggregate(
    years: string,
    statTypes: string = 'batting,pitching,fielding'
  ): Promise<JobEnqueuedResponse> {
    const response = await apiClient.post<JobEnqueuedResponse>('/admin/jobs/aggregate', null, {
      params: { years, stat_types: statTypes }
    })
    return response.data
  },

  async triggerIngest(
    source: string,
    years: string,
    entityTypes: string = 'bootstrap,players,games,plays'
  ): Promise<JobEnqueuedResponse> {
    const response = await apiClient.post<JobEnqueuedResponse>('/admin/jobs/ingest', null, {
      params: { years, source, entity_types: entityTypes }
    })
    return response.data
  },

  async triggerReadinessCheck(years?: string): Promise<JobEnqueuedResponse> {
    const response = await apiClient.post<JobEnqueuedResponse>(
      '/admin/jobs/readiness-check',
      null,
      {
        params: years ? { years } : {}
      }
    )
    return response.data
  },

  async getQueueStats(limit = 50): Promise<QueueStatsResponse> {
    const response = await apiClient.get<QueueStatsResponse>('/admin/queue/stats', {
      params: { limit }
    })
    return response.data
  },

  async getJobStatus(jobId: string): Promise<JobStatusResponse> {
    const response = await apiClient.get<JobStatusResponse>(`/admin/jobs/${jobId}`)
    return response.data
  },

  async triggerPullOdds(includeProps = false, onlyGameId?: number): Promise<JobEnqueuedResponse> {
    const response = await apiClient.post<JobEnqueuedResponse>('/admin/jobs/pull-odds', {
      include_props: includeProps,
      only_game_id: onlyGameId ?? null
    })
    return response.data
  },

  async triggerScheduleImport(
    year: number,
    gameTypes = 'R,S,F,D,L,W'
  ): Promise<JobEnqueuedResponse> {
    const response = await apiClient.post<JobEnqueuedResponse>(
      '/admin/jobs/import-schedule',
      null,
      {
        params: { year, game_types: gameTypes }
      }
    )
    return response.data
  },

  async triggerRefreshGame(gamePk: number): Promise<JobEnqueuedResponse> {
    const response = await apiClient.post<JobEnqueuedResponse>(`/admin/jobs/refresh-game/${gamePk}`)
    return response.data
  },

  async triggerSimulateGame(gameId: number, nSims = 1000): Promise<JobEnqueuedResponse> {
    const response = await apiClient.post<JobEnqueuedResponse>(
      `/admin/jobs/simulate/${gameId}`,
      null,
      {
        params: { n_sims: nSims }
      }
    )
    return response.data
  },

  async triggerSimulateSeason(
    year: number,
    nSims = 1000,
    force = true
  ): Promise<JobEnqueuedResponse> {
    const response = await apiClient.post<JobEnqueuedResponse>(
      '/admin/jobs/simulate-season',
      null,
      {
        params: { year, n_sims: nSims, force }
      }
    )
    return response.data
  }
}

export default adminApi
